import { Injectable, inject, signal, computed, effect, DestroyRef, Injector } from '@angular/core';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { BildirimTercihleriService } from './bildirim-tercihleri.service';

export type BildirimOlayTipi = 'kart_olusturuldu' | 'kart_guncellendi' | 'alt_kart_eklendi' | 'yorum_eklendi' | 'atandi' | 'bahsedildi' | 'kart_silindi' | 'hatirlatma' | 'duyuru';

export type BildirimOnceligi = 'acil' | 'normal' | 'dusuk';

export interface IBildirim {
  _id?: string;
  aliciId: string;
  gondericId?: string;
  gondericIsim?: string;
  olay: BildirimOlayTipi;
  kartId?: string;
  kartBaslik?: string;
  mesaj: string;
  okudu: boolean;
  tarih: string;
  oncelik?: BildirimOnceligi;
  okunduTarih?: string;
  hatirlatmaTarihi?: string;
  hatirlatmaAktif?: boolean;
  epostaGonderildi?: boolean;
}

export interface IBildirimFiltre {
  olay?: BildirimOlayTipi;
  okudu?: boolean;
}

export interface IOlayTipBilgi {
  ikon: string;
  etiket: string;
  renk: string;
  nzRenk: string;
}

export interface IBildirimGrubu {
  kartId: string;
  kartBaslik?: string;
  bildirimler: IBildirim[];
  sonBildirim: IBildirim;
  toplam: number;
  okunmamis: number;
}

const DB_AGGREGATE = 'FikoYT.sistem.DB.hareketler.aggregate';
const DB_GUNCELLE = 'FikoYT.sistem.DB.hareketler.guncelle';
const DB_SIL = 'FikoYT.sistem.DB.hareketler.sil';
const DB_EKLE = 'FikoYT.sistem.DB.hareketler.ekle';
const VT_BILDIRIM = 'FikoYT.sistem.bildirimler';

const OLAY_TIPI_HARITASI: Record<string, IOlayTipBilgi> = {
  kart_olusturuldu: { ikon: 'file-add', etiket: 'Oluşturuldu', renk: '#1890ff', nzRenk: 'blue' },
  kart_guncellendi: { ikon: 'edit', etiket: 'Güncellendi', renk: '#fa8c16', nzRenk: 'orange' },
  alt_kart_eklendi:  { ikon: 'paper-clip', etiket: 'Alt Kart', renk: '#13c2c2', nzRenk: 'cyan' },
  yorum_eklendi:     { ikon: 'message', etiket: 'Yorum', renk: '#52c41a', nzRenk: 'green' },
  atandi:            { ikon: 'user', etiket: 'Atandı', renk: '#722ed1', nzRenk: 'purple' },
  bahsedildi:        { ikon: 'highlight', etiket: 'Bahsedildi', renk: '#eb2f96', nzRenk: 'magenta' },
  kart_silindi:      { ikon: 'delete', etiket: 'Silindi', renk: '#ff4d4f', nzRenk: 'red' },
  hatirlatma:        { ikon: 'clock-circle', etiket: 'Hatırlatma', renk: '#faad14', nzRenk: 'gold' },
  duyuru:            { ikon: 'notification', etiket: 'Duyuru', renk: '#2f54eb', nzRenk: 'geekblue' },
};

const SON_GORULME_KEY = 'fikoyt-bildirim-son-gorulme';
const SAYFA_LIMITI = 20;

@Injectable({ providedIn: 'root' })
export class BildirimService {
  private fikoYT = inject(FikoYTService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  tercihSvc = inject(BildirimTercihleriService);

  bildirimler = signal<IBildirim[]>([]);
  okunmamisSayisi = signal(0);
  yukleniyor = signal(false);
  dahaFazlaVar = signal(true);
  private mevcutSayfa = 0;

  grupluBildirimler = computed<IBildirimGrubu[]>(() => {
    const liste = this.bildirimler();
    const gruplar = new Map<string, IBildirim[]>();
    for (const b of liste) {
      const key = b.kartId || b._id || '';
      if (!gruplar.has(key)) gruplar.set(key, []);
      gruplar.get(key)!.push(b);
    }
    return Array.from(gruplar.entries()).map(([kartId, items]) => ({
      kartId,
      kartBaslik: items[0].kartBaslik,
      bildirimler: items,
      sonBildirim: items[0],
      toplam: items.length,
      okunmamis: items.filter(b => !b.okudu).length,
    }));
  });

  private hatirlatmaInterval: any = null;

  constructor() {
    this.socketDinle();
    this.bildirimleriYukle();
    this.pushBaslat();
    effect(() => { this.tabBasliginiGuncelle(); });
    const hatirlatmaTimeout = setTimeout(() => this.hatirlatmalariKontrolEt(), 3000);
    this.hatirlatmaInterval = setInterval(() => this.hatirlatmalariKontrolEt(), 60_000);
    this.destroyRef.onDestroy(() => {
      clearTimeout(hatirlatmaTimeout);
      clearInterval(this.hatirlatmaInterval);
    });
  }

  private socketDinle() {
    try {
      const sub = this.fikoYT.io.fromEvent<IBildirim>('FikoYT.sistem.io.bildirim.yeni').subscribe(bildirim => {
        if (!bildirim) return;
        if (bildirim._id && this.bildirimler().some(b => b._id === bildirim._id)) return;
        this.bildirimler.update(liste => [bildirim, ...liste]);
        this.okunmamisSayisi.update(s => s + 1);
        if (this.tercihSvc.olayAktifMi(bildirim.olay)) {
          this.oncelikBazliBildirimUygula(bildirim);
        }
      });
      this.destroyRef.onDestroy(() => sub.unsubscribe());
    } catch {}
  }

  private oncelikBazliBildirimUygula(bildirim: IBildirim) {
    const oncelik = bildirim.oncelik || 'normal';
    const kural = this.tercihSvc.oncelikKuraliAl(oncelik);
    if (!this.tercihSvc.sessizSaatteMi()) {
      if (kural.ses && this.tercihSvc.tercihler().sesAcik) this.bildirimSesiCal();
      if (kural.masaustu && this.tercihSvc.tercihler().masaustuAcik) this.masausteBildirimGoster(bildirim);
    }
  }

  private get aktifKullaniciId(): string | null {
    return this.fikoYT.kimlik.aktifKullaniciJson()?._id ?? null;
  }

  olayTipBilgi(olay: string): IOlayTipBilgi {
    return OLAY_TIPI_HARITASI[olay] || { ikon: 'bell', etiket: olay, renk: '#8c8c8c', nzRenk: 'default' };
  }

  private bildirimSesiCal() {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      osc.frequency.setValueAtTime(600, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  }

  private async masausteBildirimGoster(bildirim: IBildirim) {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(bildirim.gondericIsim || 'FikoYT', {
        body: bildirim.mesaj,
        icon: '/simgeler/logo.webp',
        tag: bildirim._id || 'bildirim',
        silent: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
      setTimeout(() => n.close(), 5000);
    } catch {}
  }

  private tabBasliginiGuncelle() {
    if (!this.tercihSvc.tercihler().tabSayaciAcik) return;
    const sayi = this.okunmamisSayisi();
    const orijinalBaslik = document.title.replace(/^\(\d+\)\s*/, '');
    document.title = sayi > 0 ? `(${sayi}) ${orijinalBaslik}` : orijinalBaslik;
  }

  async bildirimleriYukle(filtre?: IBildirimFiltre): Promise<void> {
    if (!this.aktifKullaniciId) return;
    this.yukleniyor.set(true);
    this.mevcutSayfa = 0;
    try {
      const match: any = { vt: VT_BILDIRIM, aliciId: this.aktifKullaniciId };
      if (filtre?.olay) match.olay = filtre.olay;
      if (filtre?.okudu !== undefined) match.okudu = filtre.okudu;
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_AGGREGATE });
      istek.parametreler = { aggregate: [
        { $match: match }, { $sort: { tarih: -1 } }, { $limit: SAYFA_LIMITI }
      ]};
      await istek.isteSync();
      const liste = (istek.cevap?.sonuc || []) as IBildirim[];
      this.bildirimler.set(liste);
      this.dahaFazlaVar.set(liste.length >= SAYFA_LIMITI);
      const istek2 = new FikoYTIstekKok({ metod: 'POST', komut: DB_AGGREGATE });
      istek2.parametreler = { aggregate: [
        { $match: { vt: VT_BILDIRIM, aliciId: this.aktifKullaniciId, okudu: false } },
        { $count: 'sayi' }
      ]};
      await istek2.isteSync();
      this.okunmamisSayisi.set((istek2.cevap?.sonuc as any)?.[0]?.sayi || 0);
    } catch {} finally { this.yukleniyor.set(false); }
  }

  async dahaFazlaYukle(filtre?: IBildirimFiltre): Promise<void> {
    if (!this.aktifKullaniciId || this.yukleniyor()) return;
    this.mevcutSayfa++;
    this.yukleniyor.set(true);
    try {
      const match: any = { vt: VT_BILDIRIM, aliciId: this.aktifKullaniciId };
      if (filtre?.olay) match.olay = filtre.olay;
      if (filtre?.okudu !== undefined) match.okudu = filtre.okudu;
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_AGGREGATE });
      istek.parametreler = { aggregate: [
        { $match: match }, { $sort: { tarih: -1 } },
        { $skip: this.mevcutSayfa * SAYFA_LIMITI }, { $limit: SAYFA_LIMITI }
      ]};
      await istek.isteSync();
      const yeniler = (istek.cevap?.sonuc || []) as IBildirim[];
      this.bildirimler.update(liste => [...liste, ...yeniler]);
      this.dahaFazlaVar.set(yeniler.length >= SAYFA_LIMITI);
    } catch {} finally { this.yukleniyor.set(false); }
  }

  async okunduIsaretle(bildirimId: string): Promise<void> {
    const bildirim = this.bildirimler().find(b => b._id === bildirimId);
    if (!bildirim || bildirim.okudu) return;
    const okunduTarih = new Date().toISOString();
    this.bildirimler.update(liste => liste.map(b => b._id === bildirimId ? { ...b, okudu: true, okunduTarih } : b));
    this.okunmamisSayisi.update(s => Math.max(0, s - 1));
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_GUNCELLE });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify({ filter: { _id: bildirimId, vt: VT_BILDIRIM }, veri: { okudu: true, okunduTarih } }));
      istek.parametreler = fd;
      await istek.isteSync();
      this.bildirimLogKaydet(bildirimId, 'okundu');
    } catch {}
  }

  async okunmadiIsaretle(bildirimId: string): Promise<void> {
    const bildirim = this.bildirimler().find(b => b._id === bildirimId);
    if (!bildirim || !bildirim.okudu) return;
    this.bildirimler.update(liste => liste.map(b => b._id === bildirimId ? { ...b, okudu: false, okunduTarih: undefined } : b));
    this.okunmamisSayisi.update(s => s + 1);
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_GUNCELLE });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify({ filter: { _id: bildirimId, vt: VT_BILDIRIM }, veri: { okudu: false } }));
      istek.parametreler = fd;
      await istek.isteSync();
      this.bildirimLogKaydet(bildirimId, 'okunmadi_yapildi');
    } catch {}
  }

  async tumunuOkunduYap(): Promise<void> {
    if (!this.aktifKullaniciId) return;
    const okunduTarih = new Date().toISOString();
    this.bildirimler.update(liste => liste.map(b => ({ ...b, okudu: true, okunduTarih: b.okunduTarih || okunduTarih })));
    this.okunmamisSayisi.set(0);
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_GUNCELLE });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify({ filter: { vt: VT_BILDIRIM, aliciId: this.aktifKullaniciId, okudu: false }, veri: { okudu: true, okunduTarih }, updateMany: true }));
      istek.parametreler = fd;
      await istek.isteSync();
      this.bildirimLogKaydet('toplu', 'tumunu_okundu_yap');
    } catch {}
  }

  async bildirimSil(bildirimId: string): Promise<void> {
    this.bildirimler.update(liste => liste.filter(b => b._id !== bildirimId));
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_SIL });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify({ _id: bildirimId, vt: VT_BILDIRIM }));
      istek.parametreler = fd;
      await istek.isteSync();
      this.bildirimLogKaydet(bildirimId, 'silindi');
    } catch {}
  }

  async topluSil(idler: string[]): Promise<void> {
    if (!idler.length) return;
    this.bildirimler.update(liste => liste.filter(b => !idler.includes(b._id!)));
    try {
      for (const id of idler) {
        const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_SIL });
        const fd = new FormData();
        fd.append('req_body', JSON.stringify({ _id: id, vt: VT_BILDIRIM }));
        istek.parametreler = fd;
        await istek.isteSync();
        this.bildirimLogKaydet(id, 'silindi', 'toplu silme');
      }
    } catch {}
  }

  async bildirimGonder(aliciId: string, olay: BildirimOlayTipi, kartId: string, kartBaslik: string, mesaj: string, oncelik: 'acil' | 'normal' | 'dusuk' = 'normal'): Promise<void> {
    const gondericId = this.aktifKullaniciId;
    const gondericIsim = this.fikoYT.kimlik.aktifKullaniciJson()?.isim || '';
    const yeni: any = {
      vt: VT_BILDIRIM, aliciId, gondericId: gondericId || undefined, gondericIsim, olay,
      kartId, kartBaslik, mesaj, okudu: false, tarih: new Date().toISOString(), oncelik,
    };
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_EKLE });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify(yeni));
      istek.parametreler = fd;
      await istek.isteSync();
      const kayitliId = istek.cevap?.sonuc?._id;
      if (kayitliId) yeni._id = kayitliId;
      try { this.fikoYT.io?.emit('FikoYT.sistem.io.bildirim', yeni); } catch {}
      if (kayitliId) this.bildirimLogKaydet(kayitliId, 'gonderildi', `alici: ${aliciId}, olay: ${olay}`);
    } catch {}
  }

  sonGorulmeKaydet() {
    localStorage.setItem(SON_GORULME_KEY, new Date().toISOString());
  }

  get sonGorulmeZamani(): string | null {
    return localStorage.getItem(SON_GORULME_KEY);
  }

  gondericBasHarfleri(isim?: string): string {
    if (!isim) return '?';
    const parcalar = isim.trim().split(/\s+/).filter(p => p.length > 0);
    if (parcalar.length === 0) return '?';
    if (parcalar.length === 1) return parcalar[0][0].toUpperCase();
    return (parcalar[0][0] + parcalar[parcalar.length - 1][0]).toUpperCase();
  }

  async browserBildirimIzniIste() {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      const izin = await Notification.requestPermission();
      if (izin === 'granted') this.pushBaslat();
    }
  }

  private async pushBaslat(): Promise<void> {
    try {
      const { PushService } = await import('./push.service');
      const pushSvc = this.injector.get(PushService);
      await pushSvc.aboneDurumuKontrol();
      if (pushSvc.destekleniyor() && !pushSvc.abone()) {
        if (Notification.permission === 'granted') {
          await pushSvc.aboneOl();
        }
      }
    } catch {}
  }

  async hatirlatmaOlustur(kartId: string, kartBaslik: string, hatirlatmaTarihi: string): Promise<void> {
    if (!this.aktifKullaniciId) return;
    const yeni: any = {
      vt: VT_BILDIRIM,
      aliciId: this.aktifKullaniciId,
      gondericId: this.aktifKullaniciId,
      gondericIsim: this.fikoYT.kimlik.aktifKullaniciJson()?.isim || '',
      olay: 'hatirlatma',
      kartId, kartBaslik,
      mesaj: `"${kartBaslik || 'Kart'}" için hatırlatma`,
      okudu: false,
      tarih: new Date().toISOString(),
      oncelik: 'normal' as BildirimOnceligi,
      hatirlatmaTarihi,
      hatirlatmaAktif: true,
    };
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_EKLE });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify(yeni));
      istek.parametreler = fd;
      await istek.isteSync();
    } catch {}
  }

  private async hatirlatmalariKontrolEt(): Promise<void> {
    if (!this.aktifKullaniciId) return;
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_AGGREGATE });
      istek.parametreler = { aggregate: [
        { $match: {
          vt: VT_BILDIRIM,
          aliciId: this.aktifKullaniciId,
          olay: 'hatirlatma',
          hatirlatmaAktif: true,
          hatirlatmaTarihi: { $lte: new Date().toISOString() },
        }},
        { $limit: 10 },
      ]};
      await istek.isteSync();
      const hatirlatmalar = (istek.cevap?.sonuc || []) as IBildirim[];
      const mevcutIdler = new Set(this.bildirimler().map(b => b._id));
      for (const h of hatirlatmalar) {
        if (!mevcutIdler.has(h._id)) {
          this.bildirimler.update(liste => [h, ...liste]);
          this.okunmamisSayisi.update(s => s + 1);
          if (!this.tercihSvc.sessizSaatteMi()) {
            if (this.tercihSvc.tercihler().sesAcik) this.bildirimSesiCal();
            if (this.tercihSvc.tercihler().masaustuAcik) this.masausteBildirimGoster(h);
          }
        }
        if (h._id) {
          const guncIstek = new FikoYTIstekKok({ metod: 'POST', komut: DB_GUNCELLE });
          const fd = new FormData();
          fd.append('req_body', JSON.stringify({ filter: { _id: h._id }, veri: { hatirlatmaAktif: false } }));
          guncIstek.parametreler = fd;
          await guncIstek.isteSync();
        }
      }
    } catch {}
  }

  private bildirimLogKaydet(bildirimId: string, aksiyon: string, detay?: string) {
    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: DB_EKLE });
      const fd = new FormData();
      fd.append('req_body', JSON.stringify({
        vt: 'FikoYT.sistem.bildirim_loglari',
        bildirimId,
        kullaniciId: this.aktifKullaniciId,
        aksiyon,
        detay: detay || '',
        zaman: new Date().toISOString(),
      }));
      istek.parametreler = fd;
      istek.isteSync();
    } catch {}
  }

  static zamanOnce(tarih: string): string {
    const fark = Date.now() - new Date(tarih).getTime();
    const dk = Math.floor(fark / 60000);
    if (dk < 1) return 'Az önce';
    if (dk < 60) return dk + ' dk';
    const saat = Math.floor(dk / 60);
    if (saat < 24) return saat + ' saat';
    const gun = Math.floor(saat / 24);
    if (gun === 1) return 'Dün';
    if (gun < 7) return gun + ' gün';
    const t = new Date(tarih);
    return t.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  }
}
